<script setup>
import {computed, inject, reactive, ref} from "vue";
//import functions
import {required} from "@vuelidate/validators";
import {showMessage} from "@/utils/showMessage.js";
//import components
import {InputText, FloatLabel, Dialog, Textarea, InputNumber} from "primevue";
import {Icon} from "@iconify/vue";
import DropZoneVue from "@/views/wallet/dropZone.vue";
//import stores
import {useUserStore} from "@/stores/user.js";
import {useWalletStore} from "@/stores/wallet.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useConfirm} from "primevue/useconfirm";
import {useVuelidate} from "@vuelidate/core";
import {useI18n} from "vue-i18n";

//stores
const userStore = useUserStore();
const walletStore = useWalletStore();

//loading flags
const {
  saving,
  setSaving,
} = useLoadingFlags()

//other composables
const {isXL} = useBreakpoint();
const toast = useToast();
const confirm = useConfirm();
const {t, locale} = useI18n({useScope: 'global'})

//emits
const emit = defineEmits(['refresh', 'showUserPermissions']);

//props
const props = defineProps({
  selectedAsset: String,
  selectedUserID: Number,
  selectedUserName: String,
})

//injects
const visible = inject('isShowAddTransactionModal')

//provides

//component variables
const uploading = ref(false)
const uploadedAttachment = ref(false);
const transaction = reactive({
  "userID": props.selectedUserID != null ? props.selectedUserID : null,
  "asset": props.selectedAsset,
  "amount": "",
  "title": "",
  "note": "",
  "attachment": {
    "name": ""
  }
})
const rules = computed(() => {
  return {
    asset: {required},
    amount: {required},
    title: {required},
    note: {},
    attachment: {},
  };
});
const $vuelidate = useVuelidate(rules, transaction);

//global functions

//component functions
async function addTransaction() {
  $vuelidate.value.$touch();
  if ($vuelidate.value.$invalid) {
    return false;
  }

  setSaving(true)
  try {
    const res = await walletStore.action_addTransactions(transaction)
    if (res.result === true) {
      //toast.add({severity: 'success', summary: 'Successful', detail: `${transaction.asset} transaction added`, life: 3000});
      showMessage(`${transaction.asset} transaction added`, 'success');
      visible.value = false
      await getTransactions();
      await walletStore.action_getWallets()
    } else {
      //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 3000});
      showMessage(res, 'error');
    }
  } catch (err) {
    //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 3000});
    showMessage(err, 'error');
  } finally {
    setSaving(false)
  }
}

async function getTransactions() {
  if (props.selectedUserID != null) {
    await walletStore.action_getTransactions({
      userID: props.selectedUserID,
      asset: props.selectedAsset
    })
  } else {
    await walletStore.action_getTransactions({
      asset: props.selectedAsset
    })
  }
}

function getAttachmentFileName(fileName) {
  transaction.attachment.name = fileName
}

function attachmentUploaded(fileName) {
  getAttachmentFileName(fileName);
  uploadedAttachment.value = true
  setSaving(false)
}

function removedAttachment() {
  transaction.attachment.name = ''
  uploadedAttachment.value = false
}

//hooks

</script>
<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="min-w-full min-h-full flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        {{ $t('message.add transaction') }}
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm min-w-80 max-w-full">
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="asset" :value="transaction.asset" autocomplete="off" readonly disabled
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm p-filled !border-slate-800"/>
            <label for="asset" class="!bg-slate-900 !text-slate-500">{{ $t('message.asset.label') }}</label>
          </FloatLabel>
        </div>
        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="user" :value="props.selectedUserName" autocomplete="off" readonly disabled
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm p-filled !border-slate-800"/>
            <label for="user" class="!bg-slate-900 !text-slate-500">User</label>
          </FloatLabel>
        </div>
      </div>
      <div :dir="locale === 'fa' ? 'rtl' : 'ltr'" class="grid grid-cols-2 gap-4"
           :class="locale === 'fa' ? 'text-right' : 'text-left'">
        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="title" v-model="transaction.title" autocomplete="off"
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                       :class="{ 'has-error': $vuelidate.title.$error }"
            />
            <label for="title" class="!bg-slate-900"
                   :class="{ 'has-error': $vuelidate.title.$error }">Title</label>
          </FloatLabel>
          <template v-if="$vuelidate.title.$error">
            <p class="text-red-300 mt-1 text-xs" v-if="!$vuelidate.title.required.$response">
              This field is required
            </p>
          </template>
        </div>
        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputNumber v-model="transaction.amount" inputId="amount" :maxFractionDigits="5" fluid autocomplete="off"
                         input-class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                         :input-class="{ 'has-error': $vuelidate.amount.$error }"/>
            <!--            <InputText v-allow-digits-dot id="amount" v-model="transaction.amount" autocomplete="off"
                                   class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                   :class="{ 'has-error': $vuelidate.amount.$error }"
                        />-->
            <label for="amount" class="!bg-slate-900"
                   :class="{ 'has-error': $vuelidate.amount.$error }">{{ $t('message.amount') }}</label>
          </FloatLabel>
          <template v-if="$vuelidate.amount.$error">
            <p class="text-red-300 mt-1 text-xs" v-if="!$vuelidate.amount.required.$response">
              This field is required
            </p>
            <p class="text-red-300 mt-1 text-xs" v-if="!$vuelidate.amount.numeric.$response">
              This field should be a number
            </p>
          </template>
        </div>
        <div class="col-span-2 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on" class="h-full">
                      <Textarea id="notes" v-model="transaction.note" rows="5" style="resize: none"
                                class="!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700 h-full"/>
            <label for="notes" class="!bg-slate-900">Notes</label>
          </FloatLabel>
        </div>
      </div>
      <div :class="locale === 'fa' ? 'text-right' : 'text-left'">
        <label class="inline-block input-label">{{ $t('message.attachment.label') }}</label>
        <DropZoneVue @uploading="setSaving"
                     @uploaded="attachmentUploaded"
                     @uploadFailed="removedAttachment"
                     @removed="removedAttachment"
                     @fileSelected="getAttachmentFileName"
                     :desc="$t('message.attachment.placeholder')"/>
      </div>
    </div>

    <div class="flex justify-end gap-3">
      <button @click="addTransaction"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1"
              :disabled="$vuelidate.$invalid || saving">
        {{ $t('message.add') }}
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
      </button>
    </div>
  </Dialog>
</template>
<style></style>